import { Bell, ChevronDown, Cog, Info, Search, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useUserDropdown } from './hooks/useUserDropdown';

export default function UserDropdown() {
  const {
    user,
    open,
    setOpen,
    companies,
    search,
    setSearch,
    credits,
    creditsPopoverOpen,
    setCreditsPopoverOpen,
    isTracingAiEnabled,
    navigate,
    getTranslation,
    setNotificationsOpen,
    filtered,
    isImpersonating,
    handleSwitchCompany,
    handleEndImpersonate,
    currentCompanyRole,
    initials,
    companyInitials,
    avatarColor,
    getCompanyInitials,
    imgUrl,
  } = useUserDropdown();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant='ghost'
            className='h-7.5 gap-2 pl-0.75 pr-2 rounded-full'
          />
        }
      >
        <Avatar className='h-6 w-6'>
          <AvatarImage
            src={imgUrl(user?.company?.featuredImageFileUrl, 24)}
            alt={user?.company?.name}
          />
          <AvatarFallback
            delay={0}
            className='bg-primary text-primary-foreground text-xs font-semibold'
          >
            {companyInitials}
          </AvatarFallback>
        </Avatar>
        <span className='text-sm font-medium max-w-36 truncate'>
          {user?.company?.name ?? '…'}
        </span>
        <ChevronDown className='size-3 text-muted-foreground shrink-0' />
      </PopoverTrigger>

      <PopoverContent align='end' className='w-75 p-0 gap-0'>
        {/* Current company */}
        <div className='px-3 py-2.5 bg-muted/30'>
          <div className='flex items-center gap-2.5'>
            <Avatar className='h-9 w-9 shrink-0'>
              <AvatarImage
                src={imgUrl(user?.company?.featuredImageFileUrl, 36)}
                alt={user?.company?.name}
              />
              <AvatarFallback
                delay={0}
                className='bg-primary text-primary-foreground text-sm font-semibold'
              >
                {companyInitials}
              </AvatarFallback>
            </Avatar>
            <div className='min-w-0 flex-1'>
              <p className='text-sm font-semibold truncate'>
                {user?.company?.name}
              </p>
              <p className='text-xs text-muted-foreground capitalize'>
                {currentCompanyRole}
              </p>
              {user?.company?.id && (
                <button
                  type='button'
                  className='text-xs text-primary hover:underline underline-offset-4 mt-0.5'
                  onClick={() => {
                    void navigate({
                      to: '/network/$companyId',
                      params: { companyId: user.company!.id },
                    });
                    setOpen(false);
                  }}
                >
                  {getTranslation('viewProfile')}
                </button>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Company switcher */}
        {companies.length > 0 && (
          <>
            <div className='px-2 py-1.5'>
              <div className='relative'>
                <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground pointer-events-none' />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={getTranslation('searchCompanies')}
                  className='h-7 pl-7 text-xs'
                />
              </div>
            </div>

            <p className='px-3 pt-1 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
              {getTranslation('switchCompany')}
            </p>

            <div className='max-h-65 overflow-y-auto pb-1'>
              {filtered.map(company => (
                <Button
                  key={company.companyId}
                  variant='ghost'
                  className='w-full justify-start gap-2.5 px-3 py-2 h-auto text-sm font-normal rounded-none'
                  onClick={() => handleSwitchCompany(company)}
                >
                  <Avatar className='h-8 w-8 shrink-0'>
                    <AvatarFallback
                      delay={0}
                      className={`${avatarColor(company.companyId)?.bg} ${avatarColor(company.companyId)?.fg} text-xs font-semibold`}
                    >
                      {getCompanyInitials(company.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className='truncate'>{company.name}</span>
                </Button>
              ))}
            </div>

            <Separator />
          </>
        )}

        {/* AI Credits */}
        {isTracingAiEnabled && (
          <>
            <div className='px-3 py-3.5'>
              <div className='flex items-center justify-between mb-2'>
                <div className='flex items-center gap-1.5'>
                  <span className='text-sm font-semibold'>
                    {getTranslation('tracingAiCredits')}
                  </span>
                  {credits && (
                    <Popover
                      open={creditsPopoverOpen}
                      onOpenChange={setCreditsPopoverOpen}
                    >
                      <button
                        type='button'
                        onMouseEnter={() => setCreditsPopoverOpen(true)}
                        onMouseLeave={() => setCreditsPopoverOpen(false)}
                        className='flex items-center bg-transparent border-0 p-0 cursor-pointer'
                      >
                        <PopoverTrigger
                          render={
                            <button
                              type='button'
                              className='text-muted-foreground hover:text-foreground transition-colors'
                            />
                          }
                        >
                          <Info className='size-3.5' />
                        </PopoverTrigger>
                      </button>
                      <PopoverContent
                        side='bottom'
                        align='start'
                        className='w-64 p-4'
                      >
                        <p className='text-sm font-bold mb-4'>
                          {getTranslation('monthlyCredits', {
                            consumed: credits.consumed,
                            total: credits.consumed + credits.balance,
                          })}
                        </p>
                        <div className='space-y-3'>
                          <div className='flex justify-between'>
                            <span className='text-sm text-muted-foreground'>
                              {getTranslation('balance')}
                            </span>
                            <span className='text-sm'>{credits.balance}</span>
                          </div>
                          <div className='flex justify-between'>
                            <span className='text-sm text-muted-foreground'>
                              {getTranslation('consumed')}
                            </span>
                            <span className='text-sm'>{credits.consumed}</span>
                          </div>
                          <div className='flex justify-between'>
                            <span className='text-sm text-muted-foreground'>
                              {getTranslation('available')}
                            </span>
                            <span className='text-sm text-ai font-medium'>
                              {credits.balance}
                            </span>
                          </div>
                          {credits.resetsAt && (
                            <p className='text-xs text-muted-foreground pt-1'>
                              {getTranslation('resets', {
                                date: new Date(
                                  credits.resetsAt,
                                ).toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                }),
                              })}
                            </p>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                <span className='text-sm font-semibold'>
                  {credits
                    ? getTranslation('creditsLeft', { count: credits.balance })
                    : '—'}
                </span>
              </div>
              <div className='h-1.5 w-full rounded-full bg-muted overflow-hidden'>
                <div
                  className='h-full rounded-full bg-ai transition-all'
                  style={{
                    width: credits
                      ? `${Math.min(100, (credits.balance / (credits.balance + credits.consumed)) * 100)}%`
                      : '0%',
                  }}
                />
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* User info */}
        <div className='px-3 py-2.5 bg-muted/30'>
          <div className='flex items-center gap-2.5'>
            <Avatar className='h-7 w-7 shrink-0'>
              <AvatarImage
                src={imgUrl(user?.avatarFileUrl, 28)}
                alt={`${user?.firstName} ${user?.lastName}`}
              />
              <AvatarFallback
                delay={0}
                className='bg-primary text-primary-foreground text-xs font-semibold'
              >
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className='min-w-0'>
              <p className='text-xs font-semibold truncate'>
                {user?.firstName} {user?.lastName}
              </p>
              <p className='text-xs text-muted-foreground truncate'>
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Actions */}
        <div className='py-1 px-1'>
          <Button
            variant='ghost'
            className='w-full justify-start gap-2.5 px-3 h-8 text-sm font-normal'
            onClick={() => {
              void navigate({ to: '/profile' });
              setOpen(false);
            }}
          >
            <User className='size-3 text-muted-foreground shrink-0' />
            {getTranslation('profile')}
          </Button>
          <Button
            variant='ghost'
            className='sm:hidden w-full justify-start gap-2.5 px-3 h-8 text-sm font-normal relative'
            onClick={() => {
              setNotificationsOpen(true);
              setOpen(false);
            }}
          >
            <Bell className='size-3 text-muted-foreground shrink-0' />
            {getTranslation('notifications')}
            <span className='ml-auto w-1.5 h-1.5 rounded-full bg-destructive' />
          </Button>
          <Button
            variant='ghost'
            className='w-full justify-start gap-2.5 px-3 h-8 text-sm font-normal'
            onClick={() => {
              void navigate({ to: '/profile/settings' });
              setOpen(false);
            }}
          >
            <Cog className='size-3 text-muted-foreground shrink-0' />
            {getTranslation('accountSettings')}
          </Button>
          {isImpersonating && (
            <Button
              variant='ghost'
              className='w-full justify-start gap-2.5 px-3 h-8 text-sm font-normal'
              onClick={handleEndImpersonate}
            >
              <i className='fa-light fa-user-xmark text-xs text-muted-foreground shrink-0' />
              {getTranslation('endImpersonation')}
            </Button>
          )}
        </div>

        <Separator />
      </PopoverContent>
    </Popover>
  );
}
